import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { coreCall } from '../services/gatewayService';

export interface TwoFactorStatus {
  required: boolean;
  configured: boolean;
  setupCompleted: boolean;
  isSuperAdmin: boolean;
  totpEnabled: boolean;
  emailEnabled: boolean;
  method: 'totp' | 'email' | 'both' | null;
  availableMethods: ('totp' | 'email')[];
  totpVerifiedAt: string | null;
  emailVerifiedAt: string | null;
  setupCompletedAt: string | null;
}

export function useTwoFactor() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        setStatus(null);
        return;
      }

      const { data, error: fetchError } = await supabase.functions.invoke('twofa-get-status', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      if (fetchError) throw fetchError;

      setStatus(data);
    } catch (err: unknown) {
      const error = err as { message?: string };
      console.error('Error fetching 2FA status:', err);
      setError(error.message || 'Errore nel caricamento dello stato 2FA');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const generateTotpSecret = useCallback(async () => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) {
      throw new Error('Non autenticato');
    }

    const { data, error } = await supabase.functions.invoke('twofa-generate-totp-secret', {
      headers: {
        Authorization: `Bearer ${session.session.access_token}`
      }
    });

    if (error) throw error;
    return data;
  }, []);

  const verifyTotpSetup = useCallback(async (code: string) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) {
      throw new Error('Non autenticato');
    }

    const { data, error } = await supabase.functions.invoke('twofa-verify-totp-setup', {
      body: { code },
      headers: {
        Authorization: `Bearer ${session.session.access_token}`
      }
    });

    if (error) throw error;

    if (data.success) {
      await fetchStatus();
    }

    return data;
  }, [fetchStatus]);

  // Attivazione del codice via email: solo dal percorso di configurazione (lib/twofa/setupApi.ts),
  // che chiede prima il codice di conferma. La vecchia chiamata senza codice e' stata tolta.

  const disableMethod = useCallback(async (method: 'totp' | 'email') => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) {
      throw new Error('Non autenticato');
    }

    const { data, error } = await supabase.functions.invoke('twofa-disable', {
      body: { method },
      headers: {
        Authorization: `Bearer ${session.session.access_token}`
      }
    });

    if (error) throw error;

    if (data.success) {
      await fetchStatus();
    }

    return data;
  }, [fetchStatus]);

  const sendEmailCode = useCallback(async (email: string) => {
    const { data, error } = await coreCall('twofa-send-email-code', {
      body: { email }
    });

    if (error) throw error;
    return data;
  }, []);

  const verifyCode = useCallback(async (email: string, code: string, method: 'totp' | 'email') => {
    const { data, error } = await supabase.functions.invoke('twofa-verify-code', {
      body: { email, code, method }
    });

    if (error) throw error;
    return data;
  }, []);

  const adminReset = useCallback(async (targetUserId: string) => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) {
      throw new Error('Non autenticato');
    }

    const { data, error } = await supabase.functions.invoke('twofa-admin-reset', {
      body: { targetUserId },
      headers: {
        Authorization: `Bearer ${session.session.access_token}`
      }
    });

    if (error) throw error;
    return data;
  }, []);

  return {
    status,
    loading,
    error,
    refresh: fetchStatus,
    generateTotpSecret,
    verifyTotpSetup,
    disableMethod,
    sendEmailCode,
    verifyCode,
    adminReset
  };
}

// Constants for 2FA session management
const TWO_FACTOR_SESSION_KEY = '2fa_session_token';
const TWO_FACTOR_SESSION_EXPIRY_KEY = '2fa_session_expiry';

export function getTwoFactorSession(): string | null {
  const token = sessionStorage.getItem(TWO_FACTOR_SESSION_KEY);
  const expiry = sessionStorage.getItem(TWO_FACTOR_SESSION_EXPIRY_KEY);

  if (!token || !expiry) return null;

  // Check if expired
  if (new Date(expiry) < new Date()) {
    clearTwoFactorSession();
    return null;
  }

  return token;
}

export function setTwoFactorSession(token: string, expiresAt: string): void {
  sessionStorage.setItem(TWO_FACTOR_SESSION_KEY, token);
  sessionStorage.setItem(TWO_FACTOR_SESSION_EXPIRY_KEY, expiresAt);
}

export function clearTwoFactorSession(): void {
  sessionStorage.removeItem(TWO_FACTOR_SESSION_KEY);
  sessionStorage.removeItem(TWO_FACTOR_SESSION_EXPIRY_KEY);
}

// Constants for 2FA pending verification flag (localStorage - persists across tabs/reloads)
const TWO_FACTOR_PENDING_KEY = '2fa_verification_pending';
const TWO_FACTOR_PENDING_TTL = 10 * 60 * 1000; // 10 minutes

export function set2faPendingFlag(userId: string): void {
  localStorage.setItem(TWO_FACTOR_PENDING_KEY, JSON.stringify({
    userId,
    timestamp: Date.now()
  }));
}

export function get2faPendingFlag(): { userId: string; timestamp: number } | null {
  const raw = localStorage.getItem(TWO_FACTOR_PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > TWO_FACTOR_PENDING_TTL) {
      clear2faPendingFlag();
      return null;
    }
    return parsed;
  } catch {
    clear2faPendingFlag();
    return null;
  }
}

// Variante che NON auto-pulisce il flag scaduto: chi ripristina la sessione deve
// poter distinguere "flag scaduto" (verifica 2FA abbandonata → signOut fail-closed)
// da "flag assente" (login già completato → restore normale).
export function get2faPendingFlagRaw(): { userId: string; timestamp: number; expired: boolean } | null {
  const raw = localStorage.getItem(TWO_FACTOR_PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return { ...parsed, expired: Date.now() - parsed.timestamp > TWO_FACTOR_PENDING_TTL };
  } catch {
    clear2faPendingFlag();
    return null;
  }
}

export function clear2faPendingFlag(): void {
  localStorage.removeItem(TWO_FACTOR_PENDING_KEY);
}
